import React from 'react';

import { Button, Group, Modal, Stack, Text, Title } from '@mantine/core';
import useRevokeMCPConnection from 'hooks/aiConnect/useRevokeMCPConnection';
import { useStaffTracking } from 'hooks/mixpanel/useStaffTracking';
import { MCPConnection } from 'types';
import notificationHandler from 'utils/notifications';

interface RevokeConnectionModalProps {
  opened: boolean;
  onClose: () => void;
  connection: MCPConnection;
}

const RevokeConnectionModal = ({ opened, onClose, connection }: RevokeConnectionModalProps): JSX.Element => {
  const { successNotification, errorNotification } = notificationHandler();
  const revokeMutation = useRevokeMCPConnection();
  const { trackAIConnectRevoked } = useStaffTracking();

  const handleRevoke = (): void => {
    revokeMutation.mutate(connection.id, {
      onSuccess: () => {
        trackAIConnectRevoked({ connection_name: connection.name });
        successNotification('Connection revoked', 'Its access tokens have been invalidated.');
        onClose();
      },
      onError: error => {
        errorNotification('Revocation failed', error.response?.data?.message ?? 'An error occurred.');
      },
    });
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Title order={3} align='center'>
          Are you sure you want to revoke this connection?
        </Title>
      }
      centered
      size='lg'
      padding='xl'
    >
      <Stack spacing='md'>
        <Text>
          Revoking <b>{connection.name}</b> immediately cuts off its access to your gym data. The AI tool has
          to reconnect and be approved again to regain access.
        </Text>

        <Group mt='xl' px='xl' sx={{ justifyContent: 'space-evenly' }}>
          <Button onClick={onClose} disabled={revokeMutation.isLoading}>
            Go Back
          </Button>
          <Button
            variant='outline'
            data-cy='revokeConnectionConfirmButton'
            onClick={handleRevoke}
            loading={revokeMutation.isLoading}
          >
            Revoke Connection
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default RevokeConnectionModal;
